import { useMutation } from "@tanstack/react-query";
import { Text } from "@ui-kitten/components";
import Button from "components/Button";
import { setGroup, setStep } from "features/slices/shopPagePhaseTwoSlice";
import { useAppDispatch } from "hooks/useAppDispatch";
import { getSanityImage } from "lib/sanity";
import { Group } from "lib/sanity.types";
import { trackConversion } from "queries/conversion.queries";
import { useFetchTenantBrandingQuery } from "queries/tenant.queries";
import React from "react";
import { View } from "react-native";
import PortableText from "screens/Shop/components/PortableText";
import { toGiftPage } from "utils/navigationUtils";
import eventBus from "utils/eventBus";
import { SEASON_TICKET_CHECKOUT_STEP } from "../helpers";

const PlanGroups = ({
  groups,
  onPurchase,
}: {
  groups: Group[];
  onPurchase?: (groupName: string) => void;
}) => {
  const dispatch = useAppDispatch();
  const { data: branding } = useFetchTenantBrandingQuery();
  const { mutateAsync: track } = useMutation(trackConversion);

  return (
    <div className="flex flex-col justify-center items-center gap-16 lg:flex-row lg:items-start px-4">
      {groups.map((group) => {
        return (
          <View key={group.name} className="md:w-[450px]">
            <img
              src={getSanityImage(group.image.asset._ref)}
              alt=""
              className="rounded-tl-[50px] rounded-tr-[50px]"
            />

            <View
              className="bg-white border-4"
              style={{
                borderColor: group.borderColor?.hex || branding?.primaryColor,
              }}
            >
              <View
                className="p-2 w-full"
                style={{
                  backgroundColor:
                    group.borderColor?.hex || branding?.secondaryColor,
                  borderWidth: 0,
                }}
              >
                <Text
                  category="h1"
                  status="control"
                  style={{
                    textAlign: "center",
                    color:
                      group.groupNameColor?.hex || branding?.secondaryColor,
                  }}
                >
                  {group.name}
                </Text>
              </View>
              <View className="p-4 gap-6">
                <View>
                  <PortableText value={group.description}></PortableText>
                </View>

                <View>
                  <Button
                    testID="plan-group-button"
                    size="giant"
                    style={{
                      backgroundColor:
                        group.borderColor?.hex || branding?.secondaryColor,
                      borderWidth: 0,
                    }}
                    onPress={async () => {
                      await track({
                        step: "PLAN_GROUP_CLICKED",
                        flow: "SHOP_PHASE_TWO",
                      });
                      if (onPurchase) {
                        onPurchase(group.name);
                        return;
                      }
                      // For season ticket shop pages, step 2 is a dedicated checkout flow.
                      dispatch(
                        setStep(
                          group.seasonTicketPlan
                            ? SEASON_TICKET_CHECKOUT_STEP
                            : 2
                        )
                      );
                      dispatch(setGroup(group.name));
                      eventBus.emit("scroll-to-top");
                    }}
                  >
                    {group.purchaseText}
                  </Button>
                  {group.giftButtonText && (
                    <Button
                      size="giant"
                      style={{
                        backgroundColor:
                          group.borderColor?.hex || branding?.secondaryColor,
                        borderWidth: 0,
                        marginTop: 8,
                      }}
                      onPress={() => {
                        toGiftPage(group.giftPlanId!);
                      }}
                    >
                      {group.giftButtonText}
                    </Button>
                  )}
                </View>
              </View>
            </View>
          </View>
        );
      })}
    </div>
  );
};

export default PlanGroups;
